import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Chip, LinearProgress, Paper, TextField, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Header from "../../components/Header";
import { authFetch } from "../../Utils/apiClient";

// Training documents for teams, on the website at jcgn.org/training/<slug>.
// Each one opens with its own password, set here. Send trainees the link
// and the password; they can read it in their own language.

const SITE = "https://jcgn.org";

const DocCard = ({ doc, onSaved }) => {
  const link = `${SITE}/training/${doc.slug}`;
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await authFetch(`/training/${doc.slug}/password`, {
        method: "PUT",
        body: JSON.stringify({ password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Could not save the password");
      setPassword("");
      setMessage({ severity: "success", text: "Password saved. Send it to the trainees with the link." });
      onSaved();
    } catch (error) {
      setMessage({ severity: "error", text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setMessage({ severity: "info", text: "Copied" });
    } catch {
      setMessage({ severity: "info", text: text });
    }
  };

  return (
    <Paper sx={{ p: 2.5, mb: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {doc.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {doc.summary}
          </Typography>
          {doc.updated && (
            <Typography variant="caption" color="text.secondary">
              Content updated: {doc.updated}
            </Typography>
          )}
        </Box>
        <Chip
          color={doc.hasPassword ? "success" : "warning"}
          label={doc.hasPassword ? "Open with password" : "No password yet: closed"}
        />
      </Box>

      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
        <TextField size="small" value={link} InputProps={{ readOnly: true }} sx={{ flex: "1 1 320px" }} label="Link to send" />
        <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={() => copy(link)}>
          Copy link
        </Button>
        <Button variant="outlined" startIcon={<OpenInNewIcon />} href={link} target="_blank" rel="noopener">
          Open
        </Button>
      </Box>

      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
        <TextField
          size="small"
          type="text"
          autoComplete="off"
          label={doc.hasPassword ? "New password" : "Password"}
          helperText="At least 6 characters. Changing it closes the document for anyone with the old password."
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          sx={{ flex: "1 1 260px" }}
        />
        <Button variant="contained" disabled={saving || password.trim().length < 6} onClick={save} sx={{ mb: 2.5 }}>
          {saving ? "Saving…" : doc.hasPassword ? "Change password" : "Set password"}
        </Button>
      </Box>
      {message && (
        <Alert severity={message.severity} sx={{ mt: 1 }}>
          {message.text}
        </Alert>
      )}
    </Paper>
  );
};

const TrainingDocs = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await authFetch("/training");
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Could not load the training documents");
      setDocs(data.docs || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box m="20px">
      <Header
        title="TRAINING DOCUMENTS"
        subtitle="Send trainees the link and the password. They can choose their language on the page."
      />
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {docs.map((doc) => (
        <DocCard key={doc.slug} doc={doc} onSaved={load} />
      ))}
    </Box>
  );
};

export default TrainingDocs;
